import React, { useContext, useEffect, useState } from "react";
import DashboardAdapter from "../../../../pages/dashboard/adapter";
import { ErrorContext } from "../../../error/provider";
import DashboardAPI from "../../../../api/commercial/dashboard";
import {
  BasicAccountApplicationDocument,
  DashboardDocument,
} from "../../../../api/commercial/generated-types";
import { ProductRefType } from "../../../../context/product-context/types";
import { ProductContext } from "../../../../context/product-context/productContext";
import { ApplicationFlowSteps } from "../application-flow/types";
import { useScrollToTop } from "../../../../pages/shared/hooks/useScrollToTop";

export default function DashboardFlow({
  reloadState,
  addAdditionalCardApplication,
}: {
  reloadState: () => void;
  addAdditionalCardApplication: (
    basicAccountApplicationRef: string,
    productRef: string,
    companyName: string,
    isSoleProprietorship: boolean,
    hasDecentralizedInvoicing: BasicAccountApplicationDocument["hasDecentralizedInvoicing"],
    limit: number,
  ) => void;
}): React.JSX.Element {
  const [dashboardDocument, setDashboardDocument] =
    useState<DashboardDocument>(null);
  const [shouldRenderDashboard, setShouldRenderDashboard] = useState(false);

  const { selectedProduct } = useContext(ProductContext);
  const { setError } = useContext(ErrorContext);

  useScrollToTop(ApplicationFlowSteps.DASHBOARD);

  useEffect(() => {
    try {
      DashboardAPI.query().then((dashboardData: DashboardDocument) => {
        setDashboardDocument(dashboardData);
      });
    } catch (e) {
      setError(e);
    }
  }, [setError]);

  useEffect(() => {
    if (dashboardDocument) {
      if (selectedProduct) {
        const startBasicApplication = async (product: ProductRefType) => {
          try {
            await DashboardAPI.createBasicAccountApplication({
              productRef: product,
            });
          } catch (e) {
            setError(e);
          } finally {
            reloadState();
          }
        };

        const shouldCreateNewApplication =
          dashboardDocument.basicAccountApplications.every(
            (app) => app.productRef !== selectedProduct?.productRef,
          );

        if (shouldCreateNewApplication) {
          setShouldRenderDashboard(false);
          startBasicApplication(selectedProduct?.productRef);
        } else {
          setShouldRenderDashboard(true);
        }
      } else {
        setShouldRenderDashboard(true);
      }
    }
  }, [dashboardDocument, selectedProduct, setError, reloadState]);

  return (
    shouldRenderDashboard &&
    dashboardDocument && (
      <DashboardAdapter
        dashboardDocument={dashboardDocument}
        addAdditionalCardApplication={addAdditionalCardApplication}
      />
    )
  );
}
